import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const EMPTY = { name: "", category: "", grade: "", metal_type: "MS Products", supplier: "", location: "", state: "", price: "", quantity_available: "", min_order: "1", delivery_days: "7", thickness: "N/A", width: "N/A", certifications: "", description: "" };
const FIELDS = [
  ["name", "Product name *", "HR Coil - E250 (IS 2062)"], ["category", "Category *", "HR Coil"], ["grade", "Grade *", "E250"],
  ["supplier", "Supplier *", "Tata Steel"], ["state", "State *", "Jharkhand"], ["location", "Location", "Jamshedpur, Jharkhand"],
  ["price", "Price ₹/MT *", "48500"], ["quantity_available", "Stock (MT)", "5000"], ["min_order", "Min order (MT)", "25"],
  ["delivery_days", "Delivery days", "5"], ["thickness", "Thickness", "1.6-25mm"], ["width", "Width", "900-1850mm"],
  ["certifications", "Certifications (comma separated)", "IS 2062, ASTM A36"],
];
const inputCls = "bg-ib-bg border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm";

export function AddProductDialog({ api, headers, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        price: Number(form.price),
        quantity_available: Number(form.quantity_available || 0),
        min_order: Number(form.min_order || 1),
        delivery_days: Number(form.delivery_days || 7),
        certifications: form.certifications.split(",").map((c) => c.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${api}/products`, body, { headers: headers() });
      toast.success(`Added "${data.name}" (ID ${data.id}) — live on the marketplace`);
      setForm(EMPTY);
      setOpen(false);
      onAdded();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please fill all required fields");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button data-testid="add-product-btn" onClick={() => setOpen(true)} variant="outline" size="sm" className="border-ib-lime/40 text-ib-lime hover:bg-ib-lime/10 font-outfit text-xs gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Add product
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ib-surface border-ib-steel/30 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-ib-text">Add marketplace product</DialogTitle>
            <DialogDescription className="font-manrope text-ib-text-secondary text-xs">New products appear instantly on the marketplace with rates in ₹/MT.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className={k === "name" || k === "certifications" ? "sm:col-span-2" : ""}>
                <label className="font-outfit text-[11px] text-ib-text-secondary mb-1 block">{label}</label>
                <Input data-testid={`new-product-${k}`} value={form[k]} onChange={set(k)} placeholder={ph} required={label.includes("*")}
                  type={["price", "quantity_available", "min_order", "delivery_days"].includes(k) ? "number" : "text"} className={inputCls} />
              </div>
            ))}
            <div>
              <label className="font-outfit text-[11px] text-ib-text-secondary mb-1 block">Metal type *</label>
              <select data-testid="new-product-metal_type" value={form.metal_type} onChange={set("metal_type")} className="h-9 w-full rounded-md border border-ib-steel/30 bg-ib-bg px-3 font-manrope text-sm text-ib-text">
                {["MS Products", "GI Products", "SS Products"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="font-outfit text-[11px] text-ib-text-secondary mb-1 block">Description</label>
              <Textarea data-testid="new-product-description" value={form.description} onChange={set("description")} rows={2} className={inputCls} placeholder="Short description shown on the product card" />
            </div>
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-ib-text-secondary font-outfit text-xs">Cancel</Button>
              <Button type="submit" data-testid="new-product-submit" disabled={busy} className="bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit text-xs gap-1.5">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Add product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
